import AddIcon from "@mui/icons-material/Add";
import { Box, Button, Stack, Typography } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useCurrency } from "../../../context/CurrencyContext";
import { SettlementRange } from "../../../shared/CaseModel";
import { amountAt, MIDPOINT } from "../../../shared/settlementProspects";
import { TABULAR_NUMERALS } from "../../general/dataAtoms/shared";
import { SectionLabel } from "../../general/SectionLabel";
import { Marked } from "../../guide/GuideMarker";
import { PercentSlider } from "./PercentSlider";

/** Keeps the labels under the slider as wide as it is, its figures and button aside. */
const LABELS_INSET = 360;
/** Wide enough for any amount, so the slider does not move as it grows. */
const AMOUNT_WIDTH = 140;

/**
 * Where in the bargaining range the two are expected to settle, at the current
 * stage while no offer is on the table, and the offer that follows from it.
 */
export const RangePanel = ({
  partyName,
  opponentName,
  range,
  position,
  readOnly,
  onChange,
  onCreate,
}: {
  partyName: string;
  opponentName: string;
  // undefined: the stage has no range the two could agree in
  range: SettlementRange | undefined;
  position: number;
  readOnly: boolean;
  onChange: (position: number) => void;
  onCreate: (amount: number | undefined) => void;
}) => {
  const { currency } = useCurrency();
  const { t } = useTranslation(undefined, {
    keyPrefix: "case.analytics.settlement.prospects",
  });
  const [dragged, setDragged] = useState<number>();
  const amount = amountAt(range, dragged ?? position);
  const hasRange = amount !== undefined && range !== undefined;

  const create = (
    <Button
      variant="contained"
      startIcon={<AddIcon />}
      disabled={readOnly}
      onClick={() => onCreate(amount)}
      sx={{ flex: "none", textTransform: "none", fontWeight: 600 }}
    >
      {t("create")}
    </Button>
  );

  return (
    <Box
      sx={{
        mx: 2,
        mt: 0.5,
        mb: 1.75,
        px: 2,
        py: 1.75,
        border: 1,
        borderColor: "divider",
        borderRadius: 2,
        bgcolor: "background.paper",
      }}
    >
      {!hasRange ? (
        <Stack
          direction="row"
          spacing={2}
          sx={{ alignItems: "center", justifyContent: "space-between" }}
        >
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            {t("noRange")}
          </Typography>
          {create}
        </Stack>
      ) : (
        <>
          <Marked entry="position">
            <SectionLabel component="h4">{t("rangeTitle")}</SectionLabel>
          </Marked>
          <Stack
            direction="row"
            spacing={2}
            sx={{ alignItems: "center", mt: 1 }}
          >
            <PercentSlider
              value={position}
              label={t("rangeTitle")}
              disabled={readOnly}
              onDrag={setDragged}
              onChange={(next) => {
                setDragged(undefined);
                onChange(next);
              }}
            />
            <Typography
              sx={{
                flex: "none",
                width: AMOUNT_WIDTH,
                whiteSpace: "nowrap",
                textAlign: "right",
                fontWeight: 700,
                ...TABULAR_NUMERALS,
              }}
            >
              {currency.format(amount)}
            </Typography>
            {create}
          </Stack>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              gap: 2,
              mt: 0.75,
              pr: `${LABELS_INSET}px`,
              typography: "caption",
              color: "text.secondary",
            }}
          >
            <span>
              {t("worstFor", { share: "0%", name: partyName })}
              <br />
              {currency.format(range.partyEv)}
            </span>
            <Box component="span" sx={{ textAlign: "center" }}>
              {t("midpoint")}
              <br />
              {currency.format(amountAt(range, MIDPOINT) ?? range.mid)}
            </Box>
            <Box component="span" sx={{ textAlign: "right" }}>
              {t("worstFor", { share: "100%", name: opponentName })}
              <br />
              {currency.format(range.opponentEv)}
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};
